/**
 * @feature landing-page
 * @owner das-finsuite-application-dev
 * @description Fetches AMZN stock quote via the FinSuite REST API.
 *
 * The Lambda is a thin CORS proxy that returns raw Yahoo Finance chart metadata.
 * All calculation logic (change, changePercent) lives here in the FE for easy iteration.
 *
 * Uses Amplify's `get()` which auto-signs requests with Cognito IAM credentials,
 * matching the pattern used by FINTECH_PAPI, BRASS_API, and RISK_REMEDIATOR_API.
 */

import { get } from 'aws-amplify/api';

export interface StockQuote {
  symbol: string;
  price: number;
  change: number;
  changePercent: number;
  previousClose: number;
  lastUpdated: string;
  dayHigh: number;
  dayLow: number;
  fiftyTwoWeekHigh: number;
  fiftyTwoWeekLow: number;
  volume: number;
  /** Price on hire date (if available) */
  hireDateClose?: number;
  /** Actual trading date used (may differ from hire date due to weekends) */
  hireDateActual?: string;
  /** Growth since hire date in dollars */
  hireDateChange?: number;
  /** Growth since hire date in percent */
  hireDateChangePercent?: number;
}

interface YahooChartMeta {
  symbol?: string;
  regularMarketPrice?: number;
  chartPreviousClose?: number;
  regularMarketTime?: number;
  regularMarketDayHigh?: number;
  regularMarketDayLow?: number;
  regularMarketVolume?: number;
  fiftyTwoWeekHigh?: number;
  fiftyTwoWeekLow?: number;
  hireDateClose?: number;
  hireDateActual?: string;
}

const CACHE_KEY = 'finsuite-stock-quote';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function getCached(): StockQuote | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw) as { data: StockQuote; ts: number };
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}

function setCache(data: StockQuote): void {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    /* ignore */
  }
}

function transformMeta(meta: YahooChartMeta): StockQuote | null {
  const price = meta.regularMarketPrice ?? 0;
  const previousClose = meta.chartPreviousClose ?? 0;
  if (!price) return null;

  const change = Math.round((price - previousClose) * 100) / 100;
  const changePercent = previousClose ? Math.round(((price - previousClose) / previousClose) * 10000) / 100 : 0;

  const quote: StockQuote = {
    symbol: meta.symbol ?? 'AMZN',
    price,
    change,
    changePercent,
    previousClose,
    lastUpdated: meta.regularMarketTime ? new Date(meta.regularMarketTime * 1000).toISOString() : new Date().toISOString(),
    dayHigh: meta.regularMarketDayHigh ?? 0,
    dayLow: meta.regularMarketDayLow ?? 0,
    fiftyTwoWeekHigh: meta.fiftyTwoWeekHigh ?? 0,
    fiftyTwoWeekLow: meta.fiftyTwoWeekLow ?? 0,
    volume: meta.regularMarketVolume ?? 0
  };

  // Add hire date growth if available
  if (meta.hireDateClose) {
    quote.hireDateClose = meta.hireDateClose;
    quote.hireDateActual = meta.hireDateActual;
    quote.hireDateChange = Math.round((price - meta.hireDateClose) * 100) / 100;
    quote.hireDateChangePercent = Math.round(((price - meta.hireDateClose) / meta.hireDateClose) * 10000) / 100;
  }

  return quote;
}

/**
 * Fetches the current AMZN stock quote.
 * Uses Amplify REST client (IAM-signed via Cognito Identity Pool).
 * Returns null gracefully on failure (never throws).
 * @param hireDate - Optional YYYY-MM-DD date to fetch historical close price for growth calculation
 */
export async function fetchStockQuote(hireDate?: string): Promise<StockQuote | null> {
  const cached = getCached();
  if (cached) return cached;

  try {
    const path = hireDate ? `/stock-quote?hireDate=${hireDate}` : '/stock-quote';
    const response = await get({
      apiName: 'FINSUITE_REST_API',
      path
    }).response;

    const meta = (await response.body.json()) as unknown as YahooChartMeta;
    const quote = transformMeta(meta);
    if (!quote) return null;
    setCache(quote);
    return quote;
  } catch {
    return null;
  }
}
